import { ShieldAlert } from 'lucide-react';
import type { AdvisoryCohortEntry, CarbonAdvisoryPanel } from '@siliconx/shared';
import { formatTonnes, kgToTonnes } from './carbonCategories';
import {
  CARBON_ADVISORY_AVOIDED_VS_NEW_BASELINE,
  CARBON_ADVISORY_AVOIDED_VS_NEW_LABEL,
  CARBON_ADVISORY_DISPOSAL_DEFERRED_BASELINE,
  CARBON_ADVISORY_DISPOSAL_DEFERRED_LABEL,
  CARBON_ADVISORY_DOWNSTREAM_REUSE_BASELINE,
  CARBON_ADVISORY_DOWNSTREAM_REUSE_LABEL,
  CARBON_ADVISORY_FOOTER,
  CARBON_ADVISORY_HEADER,
} from './carbonCopy';

/**
 * SIL-440 (spec §5/§6 row 2, copy §10) — the QUARANTINED advisory module.
 *
 * Four independent, deliberately redundant quarantine devices, because any
 * one of them can be defeated by a screenshot, a print stylesheet, or a
 * colour-blind reader:
 *   1. Colour family — sky/cyan, used nowhere else on a carbon surface (the
 *      official footprint owns the violet/indigo/fuchsia family; see
 *      `carbonCategories.ts`).
 *   2. A diagonal striped header band — a "do not cross" texture that
 *      survives greyscale and colour-blindness.
 *   3. The §10 header text baked INTO the panel ("Advisory — not your
 *      footprint. Never summed with the forecast."), so a cropped screenshot
 *      of the numbers still carries the disclaimer.
 *   4. The §10 completeness footer scoped to the whole panel (moved there by
 *      the legal vet precisely so it governs all three numbers rather than
 *      hanging off the one it was originally attached to).
 *
 * On-page, NEVER a drawer (SIL-425 decision): a drawer lets a reader
 * screenshot the footprint card with the advisory numbers invisible and no
 * indication they were excluded — and lets them screenshot the advisory
 * numbers with the firewall off-screen.
 *
 * Structurally this module can only ever receive `carbonAdvisory`, a SEPARATE
 * top-level response field from `carbon` — the firewall the SIL-435 kernel
 * enforces in the type system (`AdvisoryKgCo2e` is an object, not a number)
 * is preserved here by never importing the footprint series into this file.
 */
export function CarbonAdvisoryModule({
  advisory,
}: {
  advisory: CarbonAdvisoryPanel | null | undefined;
}) {
  if (!advisory) return null;

  const groups = [
    {
      key: 'avoidedVsNew',
      label: CARBON_ADVISORY_AVOIDED_VS_NEW_LABEL,
      baseline: CARBON_ADVISORY_AVOIDED_VS_NEW_BASELINE,
      entries: advisory.avoidedVsNew,
    },
    {
      key: 'disposalDeferred',
      label: CARBON_ADVISORY_DISPOSAL_DEFERRED_LABEL,
      baseline: CARBON_ADVISORY_DISPOSAL_DEFERRED_BASELINE,
      entries: advisory.disposalDeferred,
    },
    {
      key: 'downstreamReuse',
      label: CARBON_ADVISORY_DOWNSTREAM_REUSE_LABEL,
      baseline: CARBON_ADVISORY_DOWNSTREAM_REUSE_BASELINE,
      entries: advisory.downstreamReuse,
    },
  ].filter((g) => g.entries.length > 0);

  // Nothing to quarantine — a scenario with no buy/sell/itad cohorts produces
  // no advisory figures at all, and an empty quarantine box would read as a
  // surface the reader failed to populate.
  if (groups.length === 0) return null;

  return (
    <section
      data-testid="carbon-advisory-module"
      className="relative overflow-hidden rounded-xl border border-sky-300 bg-sky-50 p-5 pt-8"
    >
      {/* Device 2 — striped "do not cross" header band. Decorative only; the
          disclaimer itself is text below, never carried by the texture. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-6"
        style={{
          background:
            'repeating-linear-gradient(-45deg, rgba(14,165,233,.20) 0 8px, transparent 8px 16px)',
        }}
      />
      <div className="relative">
        <div className="mb-3 flex items-start gap-1.5 text-xs font-bold uppercase tracking-wider text-sky-800">
          <ShieldAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          <span>{CARBON_ADVISORY_HEADER}</span>
        </div>

        <div className="space-y-3">
          {groups.map((group) => (
            <AdvisoryGroup
              key={group.key}
              label={group.label}
              baseline={group.baseline}
              entries={group.entries}
            />
          ))}
        </div>

        {/* Device 4 — footer scoped to the whole panel (copy §10). */}
        <p className="mt-4 border-t border-sky-200 pt-3 text-xs leading-relaxed text-sky-900/80">
          {CARBON_ADVISORY_FOOTER}
        </p>
      </div>
    </section>
  );
}

function AdvisoryGroup({
  label,
  baseline,
  entries,
}: {
  label: string;
  baseline: string;
  entries: AdvisoryCohortEntry[];
}) {
  const totalTonnes = kgToTonnes(entries.reduce((sum, e) => sum + e.kgCo2e, 0));

  return (
    <div className="rounded-lg border border-sky-200 bg-white/70 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12.5px] font-medium text-sky-950">{label}</span>
        <span className="font-mono text-sm font-semibold tabular-nums text-sky-700">
          {formatTonnes(totalTonnes)}
        </span>
      </div>
      {/* Per-cohort breakdown — the panel total is a sum of independently
          disclosed cohort figures, never an opaque aggregate. */}
      {entries.length > 1 && (
        <ul className="mt-1.5 space-y-0.5">
          {entries.map((entry) => (
            <li
              key={entry.cohortLabel}
              className="flex items-baseline justify-between gap-2 text-xs text-sky-900/70"
            >
              <span>{entry.cohortLabel}</span>
              <span className="font-mono tabular-nums">{formatTonnes(kgToTonnes(entry.kgCo2e))}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1.5 text-xs leading-relaxed text-sky-900/60">{baseline}</p>
    </div>
  );
}
